import { createSlice } from '@reduxjs/toolkit';
import {
    getWorkingAddress, saveGoogleRoute, getRouteList,
    getOneRoute, getLatestRoute, deactivateRoute,
    getPaginatedRouteList, searchRoutes
} from './routeApi';

const initialState = {
    selectedRoute: null,
    loadingLatestRoute: false,
    latestRoute: null,
    routeMaker: null,
    routeList: [],
    routeListAll: [],
    routeListLoaded: false,
    createdMessage: null,
    errorMessage: null,
    isDetailViewOpen: false,
    isMapViewActive: false,
    pagination: null,
    searchQuery: null,
    revisedOnly: false,
    // Pagination state
    routePageIndex: 1,
    routePageCount: 1,
    routesPerPage: 10,
};

const routeSlice = createSlice({
    name: 'route',
    initialState,
    reducers: {
        // Add your reducer functions here
        setRouteMaker: (state, action) => {
            //console.log("Setting route maker", action.payload);
            state.routeMaker = action.payload;
        },
        setRouteList: (state, action) => {
            state.routeList = action.payload;
        },
        setCreatedMessage: (state, action) => {
            state.createdMessage = action.payload;
        },
        setErrorMessage: (state, action) => {
            state.errorMessage = action.payload;
        },
        setSelectedRoute: (state, action) => {
            state.selectedRoute = action.payload;
        },
        setLatestRoute: (state, action) => {
            state.latestRoute = action.payload;
        },
        setDetailViewOpen: (state, action) => {
            state.isDetailViewOpen = action.payload;
        },
        setMapViewActive: (state, action) => {
            state.isMapViewActive = action.payload;
        },
        setPagination: (state, action) => {
            state.pagination = action.payload;
        },
        // Pagination actions
        incrementRoutePageIndex: (state) => {
            if (state.routePageIndex < state.routePageCount) {
                state.routePageIndex += 1;
            }
        },
        decrementRoutePageIndex: (state) => {
            if (state.routePageIndex > 1) {
                state.routePageIndex -= 1;
            }
        },
        setRoutePageIndex: (state, action) => {
            state.routePageIndex = action.payload;
        },
        setRoutePageCount: (state, action) => {
            state.routePageCount = action.payload;
        },
        setRoutesPerPage: (state, action) => {
            state.routesPerPage = action.payload;
            state.routePageIndex = 1; // Reset to first page when changing per page
        },
        setSearchQuery: (state, action) => {
            state.searchQuery = action.payload;
            // Reset to first page when search query changes
            state.routePageIndex = 1;
        },
        setRouteRevisedOnly: (state, action) => {
            state.revisedOnly = Boolean(action.payload);
            state.routePageIndex = 1;
        },
    },
    extraReducers: {
        [getWorkingAddress.fulfilled]: (state, action) => {
            state.workingAddress = action.payload.address.Location;
        },
        [getWorkingAddress.rejected]: (state, action) => {
            state.workingAddress = null;
        },
        [saveGoogleRoute.fulfilled]: (state, action) => {
            state.selectedRoute = action.payload.route;
            state.routeList = action.payload.routeList;
            state.routeListAll = action.payload.routeList;
            state.createdMessage = action.payload.message;
        },
        [saveGoogleRoute.rejected]: (state, action) => {
            state.selectedRoute = null;
            state.routeList = [];
            state.routeListAll = [];
            state.errorMessage = action.payload.error;
        },
        [getRouteList.fulfilled]: (state, action) => {
            state.routeListAll = action.payload.routeList;
            state.routeListLoaded = true;
        },
        [getRouteList.rejected]: (state, action) => {
            state.routeListAll = [];
            state.errorMessage = action.payload.error;
            state.routeListLoaded = false;
        },
        [getOneRoute.fulfilled]: (state, action) => {
            state.selectedRoute = action.payload.route;
        },
        [getOneRoute.rejected]: (state, action) => {
            state.selectedRoute = null;
        },
        [getLatestRoute.pending]: (state) => {
            state.loadingLatestRoute = true;
        },
        [getLatestRoute.fulfilled]: (state, action) => {
            //console.log("getLatestRoute.fulfilled", action.payload);
            state.latestRoute = action.payload.route;
            state.loadingLatestRoute = false;
        },
        [getLatestRoute.rejected]: (state, action) => {
            state.latestRoute = null;
            state.loadingLatestRoute = false;
        },
        [deactivateRoute.fulfilled]: (state, action) => {
            console.log("deactivateRoute.fulfilled", action.payload);
            // Remove the deactivated route from the current routeList
            if (action.payload.route && action.payload.route._id) {
                state.routeList = state.routeList.filter(route => route._id !== action.payload.route._id);
                state.routeListAll = state.routeListAll.filter(route => route._id !== action.payload.route._id);
            }
        },
        [deactivateRoute.rejected]: (state, action) => {
            state.errorMessage = action.payload.error;
        },
        [getPaginatedRouteList.fulfilled]: (state, action) => {
            state.routeList = action.payload.routeList;
            state.pagination = action.payload.pagination;
            // Update pagination state from API response
            if (action.payload.pagination) {
                state.routePageCount = action.payload.pagination.totalPages || 1;
            }
        },
        [getPaginatedRouteList.rejected]: (state, action) => {
            state.errorMessage = action.payload.error;
        },
        [searchRoutes.fulfilled]: (state, action) => {
            state.routeList = action.payload.routeList;
            state.pagination = action.payload.pagination;
            // Update pagination state from API response
            if (action.payload.pagination) {
                state.routePageCount = action.payload.pagination.totalPages || 1;
            }
        },
        [searchRoutes.rejected]: (state, action) => {
            state.errorMessage = action.payload.error;
        },
    },
});


export const selectRouteMaker = (state) => state.route.routeMaker;
export const selectRouteList = (state) => state.route.routeList;
export const selectRouteListAll = (state) => state.route.routeListAll;
export const selectSelectedRoute = (state) => state.route.selectedRoute;
export const selectCreatedMessage = (state) => state.route.createdMessage;
export const selectErrorMessage = (state) => state.route.errorMessage;
export const selectLatestRoute = (state) => state.route.latestRoute;
export const selectLoadingLatestRoute = (state) => state.route.loadingLatestRoute;
export const selectIsDetailViewOpen = (state) => state.route.isDetailViewOpen;
export const selectIsMapViewActive = (state) => state.route.isMapViewActive;
export const selectRouteListLoaded = (state) => state.route.routeListLoaded;
export const selectPagination = (state) => state.route.pagination;
export const selectSearchQuery = (state) => state.route.searchQuery;
export const routeRevisedOnlySelector = (state) => state.route.revisedOnly;
// Pagination selectors
export const routePageIndexSelector = (state) => state.route.routePageIndex;
export const routePageCountSelector = (state) => state.route.routePageCount;
export const routesPerPageSelector = (state) => state.route.routesPerPage;

export const {
    setRouteMaker, setRouteList, setCreatedMessage, setErrorMessage,
    setSelectedRoute, setLatestRoute, setDetailViewOpen, setMapViewActive,
    setPagination, incrementRoutePageIndex, decrementRoutePageIndex,
    setRoutePageIndex, setRoutePageCount, setRoutesPerPage,
    setSearchQuery, setRouteRevisedOnly } = routeSlice.actions;

export {
    routeSlice,
    getWorkingAddress, saveGoogleRoute, getRouteList,
    getOneRoute, getLatestRoute, deactivateRoute
};
